import { useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import type { BoutiqueCardDTO, ChartDTO, CmpCellDTO, CmpColumnDTO } from "@easyshop/shared";
import { RevenueChart, cx } from "@/components/ui";
import { ChartLegend } from "@/components/PeriodBar";
import { MobileIcon } from "@/components/mobile/MobileUi";
import { usePeriod } from "@/hooks/usePeriod";
import { compareLabel } from "@/lib/period-nav";
import { eur, pct1 } from "@/lib/format";
import { useClickOutside } from "@/hooks/useClickOutside";
import { categoryKey, type CategoryOption } from "@/lib/categories";

/* Briques des écrans PC « style iOS » : cartes blanches sans bordure sur fond groupé. */

/** Carte blanche arrondie ; ombre légère au survol si elle est cliquable. */
export function PCard({ children, className, onClick }: { children: ReactNode; className?: string; onClick?: () => void }) {
  return (
    <div
      onClick={onClick}
      className={cx(
        "min-w-0 rounded-ios bg-white",
        onClick && "cursor-pointer transition-shadow hover:shadow-[0_4px_16px_rgba(20,30,20,0.1)]",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Couleur d'un écart de comparaison (hausse, baisse, sans donnée). */
export const cellTone = (c: CmpCellDTO) => (c.hasData ? (c.up ? "text-positive" : "text-danger") : "text-faint");

/** Écart en pastille (▲ 2,3 %). */
export function DeltaBadge({ delta, className }: { delta: number | null; className?: string }) {
  const up = (delta ?? 0) >= 0;
  return (
    <span
      className={cx(
        "rounded-[6px] px-[7px] py-[2px] text-[13px] font-semibold tabular",
        delta == null ? "bg-ios-fill text-faint" : up ? "bg-positive-bg text-positive" : "bg-danger-bg text-danger",
        className
      )}
    >
      {delta == null ? "—" : `${up ? "▲" : "▼"} ${pct1(delta)}`}
    </span>
  );
}

/** Écart en texte coloré (▲ 6,2 %), « — » sans référence. */
export function DeltaText({ delta, className }: { delta: number | null; className?: string }) {
  const up = (delta ?? 0) >= 0;
  return (
    <span className={cx("font-semibold tabular", delta == null ? "text-faint" : up ? "text-positive" : "text-danger", className)}>
      {delta == null ? "—" : `${up ? "▲" : "▼"} ${pct1(delta)}`}
    </span>
  );
}

/**
 * Carte CA : montant, écart, comparaisons M-1 / N-1 en haut à droite, barre
 * intégrées / franchises (propriétaire), courbe pleine (période) et pointillée (comparaison).
 */
export function CaPanel({
  label,
  ca,
  delta,
  prevCa,
  columns,
  chart,
  split,
  segments,
}: {
  label: ReactNode;
  ca: number;
  delta: number | null;
  prevCa: number | null;
  columns: CmpColumnDTO[];
  chart: ChartDTO;
  split?: { integrated: number; franchise: number };
  /** Comparaisons M-1 / N-1 des intégrées et des franchises (propriétaire). */
  segments?: { integrated: CmpColumnDTO[]; franchise: CmpColumnDTO[] };
}) {
  const s = usePeriod();
  const total = split ? split.integrated + split.franchise : 0;
  return (
    <PCard className="flex flex-col px-5 py-[18px]">
      <div className="flex items-start gap-5">
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] text-muted">{label}</div>
          <div className="mt-[2px] flex flex-wrap items-center gap-3">
            <span className="text-[38px] font-bold leading-[44px] tabular tracking-[0.3px]">{eur(ca)}</span>
            <DeltaBadge delta={delta} />
          </div>
          <div className="truncate text-[13px] text-muted">
            vs {compareLabel(s)}
            {prevCa != null ? ` · ${eur(prevCa)}` : ""}
          </div>
        </div>
        {columns.length > 0 && (
          <div className="flex flex-shrink-0 gap-5">
            {columns.map((c) => (
              <div key={c.id} className="min-w-0" title={c.title}>
                <div className="text-[11.5px] text-muted">{c.head}</div>
                <div className="text-[15px] font-semibold tabular">{c.ca.value}</div>
                <div className={cx("text-[12px] font-semibold tabular", cellTone(c.ca))}>{c.ca.delta}</div>
              </div>
            ))}
          </div>
        )}
      </div>
      {split && total > 0 && (
        <div className="mt-4">
          <div className="flex h-[6px] overflow-hidden rounded-[3px] bg-ios-fill">
            <div className="bg-positive" style={{ width: `${(split.integrated / total) * 100}%` }} />
            <div className="flex-1 bg-franchise" />
          </div>
          <div className="mt-[6px] flex justify-between gap-2 text-[12.5px]">
            <span className="text-muted">
              <span className="text-positive">●</span> Intégrées {eur(split.integrated)}
            </span>
            <span className="text-franchise">● Franchises {eur(split.franchise)}</span>
          </div>
        </div>
      )}
      {segments && (
        <div className="mt-3">
          <SegmentCmps integrated={segments.integrated} franchise={segments.franchise} />
        </div>
      )}
      <div className="mt-auto pt-2">
        <RevenueChart current={chart.current} previous={chart.previous} axis={chart.axis} height={170} />
        <ChartLegend />
      </div>
    </PCard>
  );
}

/**
 * Comparaisons M-1 / N-1 du CA par segment, dans des encadrés teintés pour ne
 * pas les confondre avec celles du total (PC et mobile).
 */
export function SegmentCmps({ integrated, franchise, compact = false }: { integrated: CmpColumnDTO[]; franchise: CmpColumnDTO[]; compact?: boolean }) {
  if (integrated.length === 0 && franchise.length === 0) return null;
  const box = (label: string, columns: CmpColumnDTO[], color: string, bg: string) =>
    columns.length === 0 ? (
      <div />
    ) : (
      <div className={cx("min-w-0 rounded-[10px]", compact ? "px-[10px] py-2" : "px-3 py-[10px]", bg)}>
        <div className={cx("font-semibold", compact ? "text-[12px]" : "text-[12.5px]", color)}>● {label}</div>
        <div className="mt-1 grid gap-3" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}>
          {columns.map((c) => (
            <div key={c.id} className="min-w-0" title={c.title}>
              <div className="text-[11px] text-muted">{c.head}</div>
              <div className={cx("truncate font-semibold tabular", compact ? "text-[13px]" : "text-[14px]")}>{c.ca.value}</div>
              <div className={cx("text-[12px] font-semibold tabular", cellTone(c.ca))}>{c.ca.delta}</div>
            </div>
          ))}
        </div>
      </div>
    );
  return (
    <div className={cx("grid grid-cols-2", compact ? "gap-2" : "gap-3")}>
      {box("Intégrées", integrated, "text-positive", "bg-positive-bg")}
      {box("Franchises", franchise, "text-franchise", "bg-franchise-bg")}
    </div>
  );
}

/** Grille 2 × 2 des KPI (Tickets, Panier moyen, Marge, % objectif), pied vs M-1 / vs N-1. */
export function KpiTiles({ items, columns }: { items: [string, string, (c: CmpColumnDTO) => CmpCellDTO][]; columns: CmpColumnDTO[] }) {
  return (
    <div className="grid grid-cols-2 gap-4">
      {items.map(([label, value, pick]) => (
        <PCard key={label} className="flex flex-col px-[18px] py-4">
          <div className="truncate text-[13px] text-muted">{label}</div>
          <div className="mt-[2px] truncate text-[30px] font-bold tabular">{value}</div>
          <div className="flex-1" />
          {columns.length > 0 && (
            <div className="mt-3 flex gap-5 border-t-[0.5px] border-ios-sep pt-[10px]">
              {columns.map((c) => {
                const cell = pick(c);
                return (
                  <div key={c.id} className="min-w-0" title={c.title}>
                    <div className="text-[11px] text-muted">{c.head}</div>
                    <div className={cx("text-[13px] font-semibold tabular", cellTone(cell))}>{cell.delta}</div>
                  </div>
                );
              })}
            </div>
          )}
        </PCard>
      ))}
    </div>
  );
}

/** Petite tuile chiffrée (bandeaux de KPI : stock, arrivages, produits). */
export function StatTile({ label, value, hint, tone }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: "danger" | "warn" }) {
  return (
    <PCard className="px-4 py-[14px]">
      <div className="truncate text-[13px] text-muted">{label}</div>
      <div className={cx("mt-[2px] truncate text-[26px] font-bold tabular", tone === "danger" && "text-danger", tone === "warn" && "text-warn")}>{value}</div>
      {hint && <div className="truncate text-[12px] text-muted">{hint}</div>}
    </PCard>
  );
}

/** Bandeau de tuiles : 4 colonnes, ou 2 × 2 en largeur réduite (jamais 3 + 1). */
export function StatRow({ children, cols = 4 }: { children: ReactNode; cols?: 3 | 4 }) {
  return <div className={cx("grid gap-4", cols === 4 ? "grid-cols-2 xl:grid-cols-4" : "grid-cols-3")}>{children}</div>;
}

// --- Niveaux de stock ---------------------------------------------------------------

export type StockLevel = "rupture" | "faible" | "surstock" | "ok";

/** Rupture (stock ≤ 2), Faible (couverture < 7 j), Surstock (> 60 j), sinon OK. */
export function stockLevel(stock: number | null, coverageDays: number | null): StockLevel {
  if (stock == null || stock <= 2) return "rupture";
  if (coverageDays != null && coverageDays < 7) return "faible";
  if (coverageDays != null && coverageDays > 60) return "surstock";
  return "ok";
}

const LEVEL: Record<StockLevel, [string, string]> = {
  rupture: ["bg-danger-bg text-danger", "Rupture"],
  faible: ["bg-warn-bg text-warn", "Faible"],
  surstock: ["bg-[oklch(0.94_0.03_250)] text-[oklch(0.45_0.12_255)]", "Surstock"],
  ok: ["bg-positive-bg text-positive", "OK"],
};

export const LEVEL_LABELS: Record<StockLevel, string> = { rupture: "Ruptures", faible: "Faibles", surstock: "Surstock", ok: "OK" };

export function LevelBadge({ level }: { level: StockLevel }) {
  const [cls, label] = LEVEL[level];
  return <span className={cx("whitespace-nowrap rounded-[6px] px-[7px] py-[2px] text-[12px] font-semibold", cls)}>{label}</span>;
}

/** « 12 j », « — » sans ventes. */
export const coverage = (d: number | null) => (d == null ? "—" : `${Math.round(d)} j`);

// --- Contrôles -------------------------------------------------------------------------

/** Champ de recherche iOS (fond gris, loupe). */
export function SearchField({ value, onChange, placeholder = "Rechercher un produit", className }: { value: string; onChange: (v: string) => void; placeholder?: string; className?: string }) {
  return (
    <label className={cx("flex h-9 items-center gap-[6px] rounded-[10px] bg-ios-fill px-[10px]", className)}>
      <MobileIcon name="search" size={15} color="oklch(0.55 0.01 150)" strokeWidth={2.2} />
      <input type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="min-w-0 flex-1 bg-transparent text-[15px]" />
    </label>
  );
}

/** Pastilles de filtre avec compteur (« Ruptures 3 »). */
export function CountChips<T extends string>({ options, value, onChange }: { options: { key: T; label: string; count?: number }[]; value: T; onChange: (v: T) => void }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={cx("min-h-[32px] whitespace-nowrap rounded-full px-[13px] text-[14px] font-medium", value === o.key ? "bg-accent text-white" : "bg-ios-fill text-ink hover:bg-[rgba(118,118,128,0.18)]")}
        >
          {o.label}
          {o.count != null && <span className={cx("ml-[6px] tabular", value === o.key ? "text-white/80" : "text-muted")}>{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

/**
 * Filtre de catégorie : un bouton « Catégorie : Toutes ▾ » qui ouvre la liste
 * alphabétique avec recherche. `value` : clé de la catégorie (`""` = toutes).
 */
export function CategoryPicker({ options, value, onChange, total }: { options: CategoryOption[]; value: string; onChange: (key: string) => void; total: number }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setSearch("");
  }, []);
  useClickOutside(ref, close, open);
  const needle = categoryKey(search);
  const shown = useMemo(() => (needle ? options.filter((o) => o.key.includes(needle)) : options), [options, needle]);
  const current = options.find((o) => o.key === value);
  const pick = (key: string) => {
    onChange(key);
    close();
  };
  const row = (key: string, label: string, count: number) => (
    <button
      key={key || "toutes"}
      type="button"
      role="option"
      aria-selected={value === key}
      onClick={() => pick(key)}
      className={cx("flex w-full items-center gap-3 px-4 py-[8px] text-left text-[14px] hover:bg-page", value === key && "bg-accent-row font-semibold")}
    >
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="flex-shrink-0 tabular text-muted">{count}</span>
    </button>
  );

  return (
    <div ref={ref} className="relative flex items-center gap-2">
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cx(
          "flex h-9 max-w-[320px] items-center gap-2 rounded-[10px] px-3 text-[14px]",
          current ? "bg-accent font-semibold text-white" : "bg-ios-fill text-ink hover:bg-[rgba(118,118,128,0.18)]",
        )}
      >
        <span className="min-w-0 truncate">
          {current ? current.label : "Catégorie : toutes"}
          <span className={cx("ml-[6px] tabular", current ? "text-white/80" : "text-muted")}>{current ? current.count : options.length}</span>
        </span>
        <span aria-hidden className="text-[11px]">
          ▾
        </span>
      </button>
      {current && (
        <button type="button" onClick={() => onChange("")} className="text-[13px] font-medium text-accent hover:underline">
          Effacer
        </button>
      )}

      {open && (
        <div className="absolute left-0 top-[calc(100%+6px)] z-50 w-[340px] max-w-[90vw] overflow-hidden rounded-[12px] border border-line bg-white shadow-menu">
          <div className="border-b border-line-soft p-2">
            <input
              autoFocus
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && shown.length === 1 && pick(shown[0].key)}
              placeholder="Rechercher une catégorie…"
              className="w-full rounded-[8px] bg-page px-3 py-2 text-[14px] outline-none"
            />
          </div>
          <div role="listbox" className="max-h-[360px] overflow-auto py-1">
            {!needle && row("", "Toutes les catégories", total)}
            {shown.length === 0 && <div className="px-4 py-3 text-[13px] text-muted">Aucune catégorie trouvée.</div>}
            {shown.map((o) => row(o.key, o.label, o.count))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Lien d'action de section (« Tout le stock › »). */
export function SectionLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} className="hover:underline">
      {children} ›
    </button>
  );
}

// --- Cartes boutiques ------------------------------------------------------------------

/** Carte boutique : ville, type, CA, écart, barre d'objectif ; clic → page boutique. */
export function BoutiqueTile({ b, onClick }: { b: BoutiqueCardDTO; onClick: () => void }) {
  const franchise = b.type === "FRANCHISE";
  return (
    <PCard onClick={onClick} className="px-4 py-[14px]">
      <div className="flex items-center gap-2">
        <span className={cx("h-2 w-2 flex-shrink-0 rounded-full", franchise ? "bg-franchise" : "bg-positive")} />
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold">{b.city}</span>
        {b.synced ? (
          <span className="flex-shrink-0 text-[12px] text-muted">{franchise ? "Franchise" : "Intégrée"}</span>
        ) : (
          <span title="Aucune synchronisation réussie depuis 48 h" className="flex-shrink-0 rounded-[6px] bg-warn-bg px-[6px] py-px text-[11px] font-semibold text-warn">
            Non synchro.
          </span>
        )}
      </div>
      <div className="mt-2 truncate text-[24px] font-bold tabular">{eur(b.ca)}</div>
      <DeltaText delta={b.delta} className="text-[13px]" />
      <div className="mt-[10px] flex items-center gap-2">
        <div className="h-[5px] flex-1 overflow-hidden rounded-[3px] bg-ios-fill">
          <div className={cx("h-full", franchise ? "bg-franchise" : "bg-positive")} style={{ width: `${Math.max(0, Math.min(100, b.objectivePct ?? 0))}%` }} />
        </div>
        <span className="flex-shrink-0 text-[11.5px] tabular text-muted">{b.objectivePct == null ? "—" : `${Math.round(b.objectivePct)} % obj.`}</span>
      </div>
    </PCard>
  );
}
